import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon } from 'lucide-react';
import { motion } from 'framer-motion';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { StageProgress, StepCard, SelectField } from '../components/form/FormStepUI';
import { degreeInterestOptions, focusOptions, specializationOptions } from '../data/degrees';
import { usePageMeta } from '../hooks/usePageMeta';
import { useLeadTracking } from '../hooks/useLeadTracking';
import { readContactParams, readMatchParams, readProfileParams } from '../utils/matchParams';

/**
 * Entry point for the match funnel. Collects the three things that determine
 * which programs we can match against, then hands off to the questionnaire.
 */
export function MatchStartPage() {
  usePageMeta('Find My Match | FindOnlineColleges', 'Compare accredited online degree programs. Get matched by career goals, schedule, and budget.');
  const navigate = useNavigate();
  // Start certification here, at the top of the funnel, so the certificate
  // covers the whole interaction rather than only the final screen.
  useLeadTracking();
  // Honor pre-populated links so campaign traffic skips choices it already made.
  const prefill = readMatchParams();
  // Anything else the link already knows about the visitor — profile answers and
  // contact details captured upstream — rides along to the questionnaire so no
  // question is asked twice.
  const carriedProfile = readProfileParams();
  const carriedContact = readContactParams();
  const [degreeInterest, setDegreeInterest] = useState(prefill.degreeInterest || '');
  const [areaOfStudy, setAreaOfStudy] = useState(prefill.areaOfStudy || '');
  const [specialization, setSpecialization] = useState(prefill.specialization || '');
  const [error, setError] = useState('');
  const programOptions = specializationOptions.filter(spec => spec.focusValue === areaOfStudy);
  const handleAreaChange = (value: string) => {
    setAreaOfStudy(value);
    // A program from the previous area would no longer be valid.
    setSpecialization('');
    setError('');
  };
  const handleContinue = () => {
    if (!degreeInterest) {
      setError('Please select the degree level you are interested in.');
      return;
    }
    if (!areaOfStudy) {
      setError('Please select an area of study.');
      return;
    }
    if (!specialization) {
      setError('Please select a program.');
      return;
    }
    setError('');
    navigate('/request-information/form', {
      state: {
        degreeInterest,
        areaOfStudy,
        specialization,
        ...carriedProfile,
        ...carriedContact
      }
    });
  };
  return <div className="min-h-screen flex flex-col bg-gray-50" data-id="element-2133" data-mp="2mjvxhqhai4ji">
      <Header data-id="element-2134" data-mp="x7lg5y3dpyt1" />
      {/* Matches the questionnaire: the step content holds the full viewport so
          the footer stays below the fold. */}
      <main className="flex-1 min-h-[85vh] py-6 sm:py-10 pb-24 sm:pb-32 px-4" data-id="element-2135" data-mp="33v2jmuwgd39e">
        <div className="max-w-3xl mx-auto" data-id="element-2136" data-mp="33x5k68v8eesd">
          <StageProgress currentStep={1} data-id="element-2137" data-mp="38unht33st7l2" />

          <div className="mt-2 mb-4" data-id="element-2138" data-mp="38tcet5i6g3v0">
            <button type="button" onClick={() => navigate('/')} className="inline-flex items-center text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors" data-id="element-2139" data-mp="2u5iz7kt3d1cf">
              <ArrowLeftIcon className="w-4 h-4 mr-2" data-id="element-2140" data-mp="2l3i3isdiancv" />
              Back
            </button>
          </div>

          <motion.div initial={{
          opacity: 0,
          y: 12
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.25,
          ease: [0.23, 1, 0.32, 1]
        }} data-id="element-2141" data-mp="2p6xxfgy6y9lv">
            <StepCard title="What would you like to study?" subtitle="Tell us the program you have in mind and we'll match you with accredited online colleges that offer it." onContinue={handleContinue} ctaLabel="Continue" info="Matching costs you nothing, and we only work with accredited partner schools. You choose whether to talk with any of them." error={error} data-id="element-2142" data-mp="9ftlxc81dnlu">
              <div className="space-y-5" data-id="element-2143" data-mp="1wia0w1leibl3">
                <SelectField id="degree-level" label="Degree Level" value={degreeInterest} onChange={v => {
                setDegreeInterest(v);
                setError('');
              }} options={degreeInterestOptions.map(opt => ({
                label: opt.type,
                value: opt.value
              }))} data-id="element-2144" data-mp="1wjl1xtngp58i" />

                <SelectField id="area-of-study" label="Area of Study" value={areaOfStudy} onChange={handleAreaChange} options={focusOptions.map(opt => ({
                label: opt.type,
                value: opt.value
              }))} data-id="element-2145" data-mp="18quqdljh0nyr" />

                <SelectField id="program" label="Program" value={specialization} onChange={v => {
                setSpecialization(v);
                setError('');
              }} disabled={!areaOfStudy} placeholder={areaOfStudy ? '-- Please Select --' : 'Select an area of study first'} options={programOptions.map(opt => ({
                label: opt.type,
                value: opt.value
              }))} data-id="element-2146" data-mp="3pechgghg4up4" />
              </div>
            </StepCard>
          </motion.div>
        </div>
      </main>
      <Footer data-id="element-2147" data-mp="2hz7ud0g5hde5" />
    </div>;
}